import { useState, useEffect } from "react";
import { TRowItem } from "../../../components/Table/TableRow";
import { useMangeTableItems } from "../../../hooks/useManageTableItems";
import { usePagination } from "../../../hooks/usePagination";
import {
  AssetAssignmentRejectionLog,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../../config/types";
import { formatDate } from "../../../utils/common";
import { MainPageBase } from "../../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import DamsTable from "../../../components/Table/CollapsibleTable";
import { assignmentApi } from "../../../services/assets/asset-assignment";
import { BadgeLabel } from "../../../components/Label";
import useAppNavigation from "../../../hooks/useAppNavigation";
import appRoutePath from "../../../config/path";
import { AssignmentRejectionsLogsFilter } from "../../../common-ui/page-filters/AssignmentRejectionsLogsFilter";
import { NoItemContent } from "../../page-bases/NoItemContent";
import { DamsEmptyPageProps } from "../../page-bases";
import { ExportModal } from "../../../components/Modals/ExportModal";
import ActionModal from "../../../components/Modals/ActionModal";
import { DamsAppAsset } from "../../../assets/registry";
import { appConfig } from "../../../config/config";
import { usePageModels } from "../../../hooks/usePageActions";

interface FiltersState {
  search?: string;
  asset_brand?: string;
  asset_serial_number?: string;
  user_email?: string;
  reason?: string;
}

const AssignmentRejectionsLogsPage = () => {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({});
  
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [pageAction, updatePageAction] = useState<PageActions>();

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<AssetAssignmentRejectionLog[]>(
    assignmentApi.getRejectedAssignments
  );

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const navigate = useAppNavigation();

  const populateDataSet = (
    data: AssetAssignmentRejectionLog[],
    pagination?: IPagination
  ) => {
    const formatted_data = data.map((record) => ({
      ...record,
      asset_brand: `${record.asset_brand}(${record.asset_model})`,
      user_email: `${record.user_email}  (${
        record.user_first_name + record.user_last_name
      })`,
      created_at: formatDate(record.created_at),
    }));
    setDataset(formatted_data);
    updatePaginationInitState(pagination);
  };

  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await assignmentApi.getRejectedAssignments({
        ...query,
        is_deleted: false,
      });
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
        if (result.data.length === 0) {
          setPageState("empty-search");
        } else {
          setPageState("main");
        }
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
      setPageState("empty-db");
    }
  };

  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearSearch() {
    setPageState("main");
    setQuerySet({ is_deleted: false });
    fetchData({ is_deleted: false });
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await assignmentApi.exportRejectedAssignments({
        file_format: 'csv',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'assignment_rejections_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await assignmentApi.exportRejectedAssignments({
        file_format: 'pdf',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'assignment_rejections_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const page_info = getEmptyPageInfo(pageState);

  const ExtraRowContent = ({ item }: { item: TRowItem }) => {
    const goDetailsPage = () => {
      navigate({
        path: appRoutePath.REJECTED_INBOUND_ASSET_ASSIGNEMENT_DETAILS_PAGE,
        params: {
          id: item.id,
        },
      });
    };
    return (
      <Stack alignItems={"center"} direction={"row"} spacing={1}>
        <BadgeLabel
          onClick={goDetailsPage}
          title={"Details"}
          tip="Click to view more details"
          colorType="blue"
        />
      </Stack>
    );
  };

  const { models, closeModel, openModel } = usePageModels();

  function toggleAlertModal() {
    closeModel();
  }

  return (
    <MainPageBase
      pageState={pageState}
      title={"Asset Assignment Rejection History"}
      subtitle={
        "A comprehensive log of all rejected asset assignment requests, including the reason for rejection, asset details, and user information."
      }
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <AssignmentRejectionsLogsFilter<AssetAssignmentRejectionLog[]>
              ftn={assignmentApi.getRejectedAssignments}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Asset Assignment Rejections"
              tableSubtitle="List of all asset assignment requests that were rejected, with detailed context."
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={6}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              extraRowComponent={ExtraRowContent}
              tableTitleBadge={
                selectedRowItems.length === 0
                  ? undefined
                  : selectedRowItems.length + " selected"
              }
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Assignment Rejections Data"
              subtitle="Choose your preferred format to export assignment rejection logs"
            />

            {/* Alert Modal */}
            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt="continue"
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
};

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Assignment Rejections Found",
    message:
      "No assignment rejection records have been logged yet. Rejections will appear here once asset assignments are rejected.",
  },
  "empty-search": {
    title: "No Assignment Rejections Found",
    message:
      "No assignment rejection records match your search criteria. Try adjusting your filters or search terms.",
  },
};

const tkeys: (keyof AssetAssignmentRejectionLog)[] = [
  "asset_brand",
  "asset_serial_number",
  "user_email",
  "created_at",
];

const columns = [
  "Brand (Model)",
  "Asset Serial Number",
  "User - Name (Email)",
  "Date Assigned",
];

const hkeys: (keyof AssetAssignmentRejectionLog)[] = [
  "reason",
  "user_telephone",
];

const hcolumns = ["Remarks", "User Telephone"];

export default AssignmentRejectionsLogsPage;